/**
 * DashboardWidgetPreferencesContext — F3/F4: Widget Visibility, Order & Persistence
 *
 * Owns the widget registry (all known widgets per persona) and the user's
 * personalisation preferences for visibility and ordering.
 *
 * F3 (complete): local-state implementation — fully functional UI for the
 *   settings cog side panel (toggle, reorder, cross-persona additions, reset).
 *
 * F4 (this task): API persistence layer wired in.
 *   - On mount / persona switch: loads from
 *       GET /api/users/{userId}/dashboard-preferences?persona={persona}
 *   - After any mutation: auto-saves (debounced 500 ms) to
 *       PUT /api/users/{userId}/dashboard-preferences?persona={persona}
 *   - Reset to default: calls
 *       DELETE /api/users/{userId}/dashboard-preferences?persona={persona}
 *   - In demo/tour data-mode (or when userId is unavailable) the API layer is
 *     skipped entirely — preferences remain in-memory only so the demo persona
 *     toggle still works without authentication.
 *   - All API errors are silently swallowed with a console.warn so a backend
 *     outage never breaks the dashboard UI.
 *
 * Curated cross-persona additions (PRD §2.2):
 *   Executive   → Billing Pipeline (Finance), WIP Aging Overview (Finance)
 *   PM          → Billing Pipeline (Finance), WIP Aging Overview (Finance),
 *                 Client Concentration Risk (Executive), Portfolio Profitability (Executive)
 *   Finance     → Projects Requiring Attention (Executive), My Projects Health (PM),
 *                 Schedule Health (PM)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { DashboardPersona } from './DashboardPersonaContext';
import { getApiBaseUrl, getDataMode } from '@/lib/data-service';
import { authFetch } from '@/lib/auth-fetch';
import { useAuth } from '@/providers/AuthProvider';

// ── Shell persona type ────────────────────────────────────────────────────────
// The widget registry only covers the two renderable dashboard shells.
// 'project_team' renders a list view, not a shell, so it is excluded here.
export type ShellPersona = 'executive' | 'project_manager';

// ── Widget registry ───────────────────────────────────────────────────────────

export interface WidgetDefinition {
  /** Stable ID — used in preferences JSONB and as React key */
  id: string;
  /** Human-readable label shown in the settings panel */
  label: string;
  /** Short description for the settings panel */
  description: string;
  /**
   * Which shell persona this widget natively belongs to.
   * Cross-persona additions have a `homePersona` different from the
   * persona whose preferences list they appear in.
   */
  homePersona: ShellPersona;
  /**
   * Whether this widget is visible by default.
   * Defaults to true when omitted. Set to false for opt-in widgets
   * (e.g. migrated Finance widgets that are off by default).
   */
  defaultVisible?: boolean;
}

/** All widgets across all shell personas, in their default render order. */
export const WIDGET_REGISTRY: Record<ShellPersona, WidgetDefinition[]> = {
  executive: [
    {
      id: 'portfolio_pulse',
      label: 'Portfolio Pulse',
      description: '4 KPI tiles — active projects, revenue MTD, margin %, at-risk count',
      homePersona: 'executive',
    },
    {
      id: 'projects_requiring_attention',
      label: 'Projects Requiring Attention',
      description: 'At-risk projects with root cause tags and PM assignment',
      homePersona: 'executive',
    },
    {
      id: 'upcoming_key_dates',
      label: 'Upcoming Key Dates',
      description: 'Billing milestones, project ends, and schedule overruns in next 30 days',
      homePersona: 'executive',
    },
    {
      id: 'portfolio_profitability',
      label: 'Portfolio Profitability',
      description: '6-month margin trend + top/bottom 3 projects by profitability',
      homePersona: 'executive',
    },
    {
      id: 'revenue_vs_cost_burn',
      label: 'Revenue vs Cost Burn',
      description: 'Grouped bar chart by month or project — planned vs actual',
      homePersona: 'executive',
    },
    {
      id: 'billing_cash_flow_health',
      label: 'Billing & Cash Flow Health',
      description: 'WIP aging strip + invoice pipeline + cash flow health score',
      homePersona: 'executive',
    },
    {
      id: 'client_concentration_risk',
      label: 'Client Concentration Risk',
      description: 'Remaining contract value distribution with concentration health badge',
      homePersona: 'executive',
    },
    {
      id: 'utilisation_snapshot',
      label: 'Utilisation Snapshot',
      description: 'Gauge + department bars + trend chip — mode-aware',
      homePersona: 'executive',
    },
    {
      id: 'headcount_delivery_capacity',
      label: 'Headcount & Delivery Capacity',
      description: '3 KPI tiles + department capacity bars',
      homePersona: 'executive',
    },
    {
      id: 'quick_links_executive',
      label: 'Quick Links',
      description: 'Shortcuts to Profitability, WIP, P&L, Budget Variance, and more',
      homePersona: 'executive',
    },
    // ── Financial detail widgets (migrated from Finance persona) ──────────────
    // All off by default — user must enable via settings cog.
    {
      id: 'finance_pulse',
      label: 'Finance Pulse',
      description: '4 KPI tiles — Total WIP, Revenue Recognised MTD, Overdue Invoices, Deferred Revenue',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'wip_aging_overview',
      label: 'WIP Aging Overview',
      description: 'AgedWipStrip + full portfolio project table + action summary bar',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'revenue_recognition_status',
      label: 'Revenue Recognition Status',
      description: 'Portfolio revenue recognition summary tiles + method breakdown table',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'billing_pipeline',
      label: 'Billing Pipeline',
      description: 'Ready to raise, upcoming, and overdue invoice queues across all projects',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'month_end_readiness',
      label: 'Month-End Readiness',
      description: '8-item month-end close checklist with proximity-aware urgency',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'over_under_billing',
      label: 'Over & Under Billing',
      description: 'Projects over-billed and under-billed above threshold, sorted by amount',
      homePersona: 'executive',
      defaultVisible: false,
    },
    {
      id: 'project_financial_summary',
      label: 'Project Financial Summary',
      description: 'Full portfolio financial table with signal chips, filters, and column toggles',
      homePersona: 'executive',
      defaultVisible: false,
    },
  ],

  project_manager: [
    {
      id: 'pm_pulse',
      label: 'My Project Pulse',
      description: '4 KPI tiles scoped to your projects — weekly delta',
      homePersona: 'project_manager',
    },
    {
      id: 'my_projects_health',
      label: 'My Projects Health',
      description: 'Full-width table with signal chips — RAG, budget burn, forecast end',
      homePersona: 'project_manager',
    },
    {
      id: 'budget_hours_burn',
      label: 'Budget & Hours Burn',
      description: 'Horizontal burn bars with completion diamond overlay',
      homePersona: 'project_manager',
    },
    {
      id: 'schedule_health',
      label: 'Schedule Health',
      description: 'Booking coverage (rich) or contracted vs forecast end dates (basic)',
      homePersona: 'project_manager',
    },
    {
      id: 'my_team_this_week',
      label: 'My Team This Week',
      description: 'Cross-project team booking status for the current week',
      homePersona: 'project_manager',
    },
    {
      id: 'upcoming_billing_events',
      label: 'Upcoming Billing Events',
      description: 'Milestones, T&M cycles, and % complete triggers in next 30 days',
      homePersona: 'project_manager',
    },
    {
      id: 'timesheet_status',
      label: 'Hours & Cost Actuals',
      description: 'Actual hours logged and cost burn vs budget across your active projects',
      homePersona: 'project_manager',
    },
    {
      id: 'quick_links_pm',
      label: 'Quick Links',
      description: 'Shortcuts to My Projects, Budget Variance, P&L, Forecast, and more',
      homePersona: 'project_manager',
    },
    // ── Financial detail widgets available to PM (scoped to their projects) ───
    // Off by default — user must enable via settings cog.
    {
      id: 'wip_aging_overview',
      label: 'WIP Aging Overview',
      description: 'WIP aging strip + project table scoped to your projects',
      homePersona: 'project_manager',
      defaultVisible: false,
    },
    {
      id: 'billing_pipeline',
      label: 'Billing Pipeline',
      description: 'Ready to raise, upcoming, and overdue invoices for your projects',
      homePersona: 'project_manager',
      defaultVisible: false,
    },
  ],
};

// ── Preferences shape ─────────────────────────────────────────────────────────

export interface WidgetPreferences {
  /** Ordered list of widget IDs — only visible widgets; hidden = omitted */
  widgetOrder: string[];
  /** IDs of widgets that are currently hidden */
  hiddenWidgets: Set<string>;
}

/**
 * Wire-format shape stored in the JSONB column on the backend.
 * `hiddenWidgets` is stored as an array for JSON serialisation.
 */
interface PersistedPreferences {
  widgetOrder: string[];
  hiddenWidgets: string[];
}

// ── Derive defaults ───────────────────────────────────────────────────────────

function buildDefaultPreferences(persona: ShellPersona): WidgetPreferences {
  const widgets = WIDGET_REGISTRY[persona];
  const ids = widgets.filter(w => w.defaultVisible !== false).map(w => w.id);
  // Widgets with defaultVisible: false start hidden so they don't clutter the
  // default dashboard — users must opt in via the settings cog.
  const defaultHidden = new Set(
    widgets.filter(w => w.defaultVisible === false).map(w => w.id),
  );
  return {
    widgetOrder: ids,
    hiddenWidgets: defaultHidden,
  };
}

/** Convert runtime preferences to wire format for the API. */
function toPersistedPreferences(prefs: WidgetPreferences): PersistedPreferences {
  return {
    widgetOrder: prefs.widgetOrder,
    hiddenWidgets: Array.from(prefs.hiddenWidgets),
  };
}

/** Convert wire format from the API back to runtime preferences. */
function fromPersistedPreferences(persisted: PersistedPreferences): WidgetPreferences {
  return {
    widgetOrder: persisted.widgetOrder ?? [],
    hiddenWidgets: new Set<string>(persisted.hiddenWidgets ?? []),
  };
}

// ── Context shape ─────────────────────────────────────────────────────────────

export interface DashboardWidgetPreferencesContextValue {
  /** Ordered widget IDs for the current persona (visible only) */
  orderedVisibleWidgets: string[];
  /** All widget IDs for the current persona (visible + hidden) */
  allWidgetIds: string[];
  /** True if a widget is currently visible */
  isWidgetVisible: (widgetId: string) => boolean;
  /** Toggle visibility for a widget */
  toggleWidgetVisibility: (widgetId: string) => void;
  /** Move a widget up one position in the order */
  moveWidgetUp: (widgetId: string) => void;
  /** Move a widget down one position in the order */
  moveWidgetDown: (widgetId: string) => void;
  /** Replace the full widget order in one call (used by drag-and-drop) */
  reorderWidgets: (newOrder: string[]) => void;
  /**
   * Add a cross-persona widget to the current persona's order.
   * No-op if already in the list.
   */
  addCrossPersonaWidget: (widgetId: string) => void;
  /** Remove a cross-persona widget from the current persona's preferences */
  removeCrossPersonaWidget: (widgetId: string) => void;
  /** Reset preferences for the current persona to the default baseline */
  resetToDefault: () => void;
  /** Whether preferences differ from the default baseline */
  hasCustomisations: boolean;
  /** True while preferences are being loaded from the API on mount */
  isLoadingPreferences: boolean;
  /** True while a save/delete request is in-flight to the API */
  isSaving: boolean;
}

export const DashboardWidgetPreferencesContext =
  createContext<DashboardWidgetPreferencesContextValue | null>(null);

// ── API helpers ───────────────────────────────────────────────────────────────

/**
 * Returns true when the API persistence layer should be active.
 * We only persist in live data-mode and when a userId is available.
 */
function shouldPersistToApi(userId: string | undefined): userId is string {
  return getDataMode() === 'live' && !!userId;
}

async function apiLoadPreferences(
  userId: string,
  persona: DashboardPersona,
): Promise<PersistedPreferences | null> {
  try {
    const url = `${getApiBaseUrl()}/users/${encodeURIComponent(userId)}/dashboard-preferences?persona=${persona}`;
    const res = await authFetch(url);
    if (res.status === 404) return null; // No preferences saved yet — use defaults
    if (!res.ok) {
      console.warn(`[DashboardPreferences] Failed to load preferences: ${res.status}`);
      return null;
    }
    const data = await res.json() as PersistedPreferences;
    return data;
  } catch (err) {
    console.warn('[DashboardPreferences] Error loading preferences:', err);
    return null;
  }
}

async function apiSavePreferences(
  userId: string,
  persona: DashboardPersona,
  prefs: PersistedPreferences,
): Promise<void> {
  try {
    const url = `${getApiBaseUrl()}/users/${encodeURIComponent(userId)}/dashboard-preferences?persona=${persona}`;
    const res = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    });
    if (!res.ok) {
      console.warn(`[DashboardPreferences] Failed to save preferences: ${res.status}`);
    }
  } catch (err) {
    console.warn('[DashboardPreferences] Error saving preferences:', err);
  }
}

async function apiResetPreferences(
  userId: string,
  persona: DashboardPersona,
): Promise<void> {
  try {
    const url = `${getApiBaseUrl()}/users/${encodeURIComponent(userId)}/dashboard-preferences?persona=${persona}`;
    const res = await authFetch(url, { method: 'DELETE' });
    if (!res.ok) {
      console.warn(`[DashboardPreferences] Failed to reset preferences: ${res.status}`);
    }
  } catch (err) {
    console.warn('[DashboardPreferences] Error resetting preferences:', err);
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

interface DashboardWidgetPreferencesProviderProps {
  children: ReactNode;
  /** The currently active persona — drives which preferences are active */
  persona: DashboardPersona;
}

export function DashboardWidgetPreferencesProvider({
  children,
  persona,
}: DashboardWidgetPreferencesProviderProps) {
  const { authenticatedUser } = useAuth();
  const userId = authenticatedUser?.id;

  // Preferences are only tracked for the three shell personas.
  // 'project_team' has no widget shell so it is excluded here.
  const [preferences, setPreferences] = useState<Record<ShellPersona, WidgetPreferences>>(
    () => ({
      executive: buildDefaultPreferences('executive'),
      project_manager: buildDefaultPreferences('project_manager'),
    }),
  );

  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Tracks which personas have already been loaded from the API in this
   * session so we don't re-fetch on every persona switch.
   */
  const loadedPersonasRef = useRef<Set<ShellPersona>>(new Set());

  /**
   * Ref to hold the debounce timer for auto-save.
   * Using a ref ensures the timer survives re-renders without being a
   * dependency of the mutator callbacks.
   */
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Map the incoming persona (which may be 'project_team') to a ShellPersona
   * for all registry/preference lookups. Project Team users don't have a
   * widget shell so their preferences fall back to 'executive' (unused in
   * practice since the settings cog is hidden for Project Team — DB-5).
   */
  const shellPersona: ShellPersona =
    persona === 'project_team' ? 'executive' : persona;

  // ── Load preferences from API on mount / persona switch ──────────────────

  useEffect(() => {
    if (persona === 'project_team') return;
    if (!shouldPersistToApi(userId)) return;
    if (loadedPersonasRef.current.has(shellPersona)) return;

    let cancelled = false;

    async function load() {
      setIsLoadingPreferences(true);
      const persisted = await apiLoadPreferences(userId as string, shellPersona);

      if (cancelled) return;

      if (persisted) {
        const loaded = fromPersistedPreferences(persisted);

        // Merge with the default: ensure any newly added widgets (added after
        // the user last saved) appear at the end so they're not silently lost.
        const defaults = buildDefaultPreferences(shellPersona);
        const knownIds = new Set([...loaded.widgetOrder, ...Array.from(loaded.hiddenWidgets)]);
        const newWidgets = defaults.widgetOrder.filter(id => !knownIds.has(id));
        if (newWidgets.length > 0) {
          loaded.widgetOrder = [...loaded.widgetOrder, ...newWidgets];
        }

        setPreferences(prev => ({ ...prev, [shellPersona]: loaded }));
      }

      loadedPersonasRef.current.add(shellPersona);
      setIsLoadingPreferences(false);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [persona, shellPersona, userId]);

  const prefs = preferences[shellPersona];

  // ── Auto-save (debounced) ─────────────────────────────────────────────────

  /**
   * Schedules a debounced save of the current persona's preferences to the
   * API. Called after every mutator action so changes are persisted
   * automatically without requiring an explicit "Save" button.
   *
   * Uses a 500 ms debounce so rapid interactions (e.g. multiple quick
   * reorders) are batched into a single API call.
   */
  const scheduleSave = useCallback(
    (updatedPrefs: WidgetPreferences) => {
      if (persona === 'project_team') return;
      if (!shouldPersistToApi(userId)) return;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(async () => {
        setIsSaving(true);
        await apiSavePreferences(
          userId as string,
          shellPersona,
          toPersistedPreferences(updatedPrefs),
        );
        setIsSaving(false);
      }, 500);
    },
    [persona, shellPersona, userId],
  );

  // ── Derived values ──────────────────────────────────────────────────────────

  const allWidgetIds = useMemo(
    () => [...prefs.widgetOrder, ...Array.from(prefs.hiddenWidgets)],
    [prefs],
  );

  const orderedVisibleWidgets = useMemo(() => prefs.widgetOrder, [prefs]);

  const isWidgetVisible = useCallback(
    (widgetId: string) => !prefs.hiddenWidgets.has(widgetId),
    [prefs],
  );

  const defaultPrefs = useMemo(() => buildDefaultPreferences(shellPersona), [shellPersona]);

  const hasCustomisations = useMemo(() => {
    if (prefs.widgetOrder.length !== defaultPrefs.widgetOrder.length) return true;
    if (prefs.hiddenWidgets.size !== defaultPrefs.hiddenWidgets.size) return true;
    for (const id of prefs.hiddenWidgets) {
      if (!defaultPrefs.hiddenWidgets.has(id)) return true;
    }
    return prefs.widgetOrder.some((id, i) => id !== defaultPrefs.widgetOrder[i]);
  }, [prefs, defaultPrefs]);

  // ── Mutators ────────────────────────────────────────────────────────────────

  const updatePersonaPrefs = useCallback(
    (updater: (prev: WidgetPreferences) => WidgetPreferences) => {
      setPreferences(prev => {
        const updated = updater(prev[shellPersona]);
        scheduleSave(updated);
        return { ...prev, [shellPersona]: updated };
      });
    },
    [shellPersona, scheduleSave],
  );

  const toggleWidgetVisibility = useCallback(
    (widgetId: string) => {
      updatePersonaPrefs(prev => {
        const hidden = new Set(prev.hiddenWidgets);
        if (hidden.has(widgetId)) {
          // Unhide — append to the end of the visible order
          hidden.delete(widgetId);
          return {
            hiddenWidgets: hidden,
            widgetOrder: [...prev.widgetOrder, widgetId],
          };
        } else {
          // Hide — remove from visible order
          hidden.add(widgetId);
          return {
            hiddenWidgets: hidden,
            widgetOrder: prev.widgetOrder.filter(id => id !== widgetId),
          };
        }
      });
    },
    [updatePersonaPrefs],
  );

  const moveWidgetUp = useCallback(
    (widgetId: string) => {
      updatePersonaPrefs(prev => {
        const order = [...prev.widgetOrder];
        const idx = order.indexOf(widgetId);
        if (idx <= 0) return prev; // already at top
        [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
        return { ...prev, widgetOrder: order };
      });
    },
    [updatePersonaPrefs],
  );

  const moveWidgetDown = useCallback(
    (widgetId: string) => {
      updatePersonaPrefs(prev => {
        const order = [...prev.widgetOrder];
        const idx = order.indexOf(widgetId);
        if (idx < 0 || idx >= order.length - 1) return prev; // already at bottom
        [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
        return { ...prev, widgetOrder: order };
      });
    },
    [updatePersonaPrefs],
  );

  const reorderWidgets = useCallback(
    (newOrder: string[]) => {
      updatePersonaPrefs(prev => ({ ...prev, widgetOrder: newOrder }));
    },
    [updatePersonaPrefs],
  );

  const addCrossPersonaWidget = useCallback(
    (widgetId: string) => {
      updatePersonaPrefs(prev => {
        if (prev.widgetOrder.includes(widgetId)) return prev;
        const hidden = new Set(prev.hiddenWidgets);
        hidden.delete(widgetId); // ensure not hidden if re-adding
        return {
          hiddenWidgets: hidden,
          widgetOrder: [...prev.widgetOrder, widgetId],
        };
      });
    },
    [updatePersonaPrefs],
  );

  const removeCrossPersonaWidget = useCallback(
    (widgetId: string) => {
      // Only remove from cross-persona — native widgets cannot be removed, only hidden
      const nativeIds = new Set(WIDGET_REGISTRY[shellPersona].map(w => w.id));
      if (nativeIds.has(widgetId)) return;
      updatePersonaPrefs(prev => {
        const hidden = new Set(prev.hiddenWidgets);
        hidden.delete(widgetId);
        return {
          hiddenWidgets: hidden,
          widgetOrder: prev.widgetOrder.filter(id => id !== widgetId),
        };
      });
    },
    [shellPersona, updatePersonaPrefs],
  );

  const resetToDefault = useCallback(() => {
    const defaults = buildDefaultPreferences(shellPersona);
    setPreferences(prev => ({ ...prev, [shellPersona]: defaults }));

    // Fire the DELETE endpoint in live mode — don't wait for debounce
    if (persona !== 'project_team' && shouldPersistToApi(userId)) {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setIsSaving(true);
      apiResetPreferences(userId as string, shellPersona).finally(() => setIsSaving(false));
    }
  }, [persona, shellPersona, userId]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const value = useMemo<DashboardWidgetPreferencesContextValue>(
    () => ({
      orderedVisibleWidgets,
      allWidgetIds,
      isWidgetVisible,
      toggleWidgetVisibility,
      moveWidgetUp,
      moveWidgetDown,
      reorderWidgets,
      addCrossPersonaWidget,
      removeCrossPersonaWidget,
      resetToDefault,
      hasCustomisations,
      isLoadingPreferences,
      isSaving,
    }),
    [
      orderedVisibleWidgets,
      allWidgetIds,
      isWidgetVisible,
      toggleWidgetVisibility,
      moveWidgetUp,
      moveWidgetDown,
      reorderWidgets,
      addCrossPersonaWidget,
      removeCrossPersonaWidget,
      resetToDefault,
      hasCustomisations,
      isLoadingPreferences,
      isSaving,
    ],
  );

  return (
    <DashboardWidgetPreferencesContext.Provider value={value}>
      {children}
    </DashboardWidgetPreferencesContext.Provider>
  );
}

// ── Consumer hook ─────────────────────────────────────────────────────────────

export function useDashboardWidgetPreferences(): DashboardWidgetPreferencesContextValue {
  const ctx = useContext(DashboardWidgetPreferencesContext);
  if (!ctx) {
    throw new Error(
      'useDashboardWidgetPreferences must be used within a DashboardWidgetPreferencesProvider',
    );
  }
  return ctx;
}
